import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, MapPin } from 'lucide-react';
import { Toaster } from 'sonner';
import { toast } from 'sonner';
import axios from 'axios';
import { activateKioskMode } from '../lib/kioskMode';
import { capturePairTokenFromUrl, getDeviceToken } from '../lib/devicePairing';
import DeviceUnauthorized from './DeviceUnauthorized';

import {
  API_BASE,
  formatEmployeeName,
  TerminalHeader,
  LocationSelection,
  EmployeeGrid,
  PinPad,
  ActionButtons,
  ActionResult,
} from './terminal';

export default function TimeTrackingTerminal() {
  const { locationId } = useParams();
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [pin, setPin] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [pinValidated, setPinValidated] = useState(false);
  const [autoLogoutCountdown, setAutoLogoutCountdown] = useState(5);
  const [deviceUnauthorized, setDeviceUnauthorized] = useState(false);

  // Clock tick
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Kiosk-Lock + Geraete-Pairing: dieser Tab ist jetzt als Terminal-Geraet
  // markiert. Solange dieser Tab offen ist, leiten unbekannte URLs (inkl.
  // /login) automatisch hierher zurueck. Zusaetzlich Pair-Token aus URL
  // einsammeln und global als axios-Header setzen.
  useEffect(() => {
    activateKioskMode(window.location.pathname);
    capturePairTokenFromUrl();
    const token = getDeviceToken();
    if (token) {
      axios.defaults.headers.common['X-Device-Token'] = token;
    }
  }, []);

  // Helper: 403 mit „Gerät" als Device-Lock interpretieren
  const detectDeviceError = useCallback((err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 403 && typeof detail === 'string' && detail.toLowerCase().includes('gerät')) {
      setDeviceUnauthorized(true);
      return true;
    }
    return false;
  }, []);

  // Auto-logout after action result
  useEffect(() => {
    if (!actionResult) return;
    setAutoLogoutCountdown(5);
    const countdown = setInterval(() => setAutoLogoutCountdown(prev => prev - 1), 1000);
    const logout = setTimeout(() => resetToEmployeeList(), 5000);
    return () => { clearInterval(countdown); clearTimeout(logout); };
  }, [actionResult]);

  // ---- Data loading ----
  const loadLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/time-tracking/terminal/locations`);
      setLocations(res.data);
    } catch (err) {
      if (!detectDeviceError(err)) toast.error('Fehler beim Laden der Standorte');
    }
    finally { setLoading(false); }
  }, [detectDeviceError]);

  const loadEmployees = useCallback(async (locId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/time-tracking/terminal/employees`, { params: { location_id: locId } });
      setEmployees(res.data);
    } catch (err) {
      if (!detectDeviceError(err)) toast.error('Fehler beim Laden der Mitarbeiter');
    }
    finally { setLoading(false); }
  }, [detectDeviceError]);

  // Load locations on mount
  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  // Load employees when location changes
  useEffect(() => {
    if (!locationId) return;
    loadEmployees(locationId);
    const loc = locations.find(l => l.id === locationId);
    if (loc) {
      setSelectedLocation(loc);
    } else {
      const loadLocationInfo = async (id) => {
        try {
          const res = await axios.get(`${API_BASE}/time-tracking/terminal/locations`);
          const found = res.data.find(l => l.id === id);
          if (found) { setSelectedLocation(found); setLocations(res.data); }
        } catch (e) { console.error('Failed to load location info:', e); }
      };
      loadLocationInfo(locationId);
    }
  }, [locationId, locations, loadEmployees]);

  // ---- Navigation / Reset ----
  const resetToEmployeeList = () => {
    setActionResult(null);
    setSelectedEmployee(null);
    setCurrentEntry(null);
    setPinValidated(false);
    setPin('');
  };

  const handleBack = () => {
    if (actionResult) { resetToEmployeeList(); }
    else if (pinValidated) { setPinValidated(false); setPin(''); }
    else if (selectedEmployee) { setSelectedEmployee(null); setPin(''); }
    else if (locationId) { navigate('/zeiterfassung/terminal'); setSelectedLocation(null); }
  };

  // ---- Employee / PIN / Action handlers ----
  const handleEmployeeSelect = (employee) => {
    if (employee.is_unavailable || !employee.has_pin) return;
    setSelectedEmployee(employee);
    setPin('');
    setPinValidated(false);
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (pin.length < 4) return;
    setActionLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/time-tracking/terminal/status/${selectedEmployee.id}`, { params: { pin } });
      setCurrentEntry(res.data);
      setPinValidated(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültige PIN');
      setPin('');
    } finally { setActionLoading(false); }
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/time-tracking/terminal/action`, null, {
        params: { employee_id: selectedEmployee.id, action, pin }
      });
      setActionResult({
        success: true, action,
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        entry: res.data
      });
      const msgs = { clock_in: 'Erfolgreich eingestempelt!', clock_out: 'Erfolgreich ausgestempelt!', break_start: 'Pause gestartet!', break_end: 'Pause beendet!' };
      toast.success(msgs[action]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally { setActionLoading(false); }
  };

  // ==================== RENDER ====================

  // 0) Device-Sperre (kein/ungueltiges Token)
  if (deviceUnauthorized) {
    return <DeviceUnauthorized />;
  }

  // 1) Location selection
  if (!locationId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-4 sm:p-6">
        <Toaster position="top-center" />
        <TerminalHeader currentTime={currentTime} />
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg sm:text-xl font-semibold text-center mb-4 sm:mb-6 flex items-center justify-center gap-2">
            <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
            Standort auswählen
          </h2>
          <LocationSelection
            locations={locations}
            loading={loading}
            onSelect={(loc) => navigate(`/zeiterfassung/terminal/${loc.id}`)}
          />
        </div>
      </div>
    );
  }

  // 2) Action result
  if (actionResult) {
    return (
      <>
        <Toaster position="top-center" />
        <ActionResult
          result={actionResult}
          employee={selectedEmployee}
          countdown={autoLogoutCountdown}
          onBack={handleBack}
        />
      </>
    );
  }

  // 3) Employee selected → PIN or actions
  if (selectedEmployee) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-2 sm:p-4 md:p-6 flex flex-col">
        <Toaster position="top-center" />
        <div className="flex items-center justify-between max-w-lg mx-auto w-full mb-2 sm:mb-4">
          <Button variant="ghost" onClick={handleBack} className="text-gray-400 hover:text-white h-8 sm:h-9 text-xs sm:text-sm px-2 sm:px-3">
            <ArrowLeft className="mr-1 h-3 w-3 sm:h-4 sm:w-4" /> Zurück
          </Button>
          <span className="text-sm sm:text-base md:text-lg font-bold">{formatEmployeeName(selectedEmployee)}</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-lg mx-auto w-full">
            {!pinValidated ? (
              <PinPad pin={pin} onPinChange={setPin} onSubmit={handlePinSubmit} loading={actionLoading} />
            ) : (
              <ActionButtons currentEntry={currentEntry} onAction={handleAction} loading={actionLoading} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // 4) Employee list for selected location
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-4 sm:p-6">
      <Toaster position="top-center" />
      <div className="max-w-4xl mx-auto mb-4 sm:mb-8">
        <Button variant="ghost" onClick={handleBack} className="mb-3 sm:mb-4 text-gray-400 hover:text-white h-8 sm:h-10 text-sm sm:text-base">
          <ArrowLeft className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" /> Standorte
        </Button>
        <div className="text-center">
          {selectedLocation && (
            <Badge variant="outline" className="mb-2 sm:mb-3 text-xs sm:text-sm text-primary border-primary/50">
              <MapPin className="h-3 w-3 mr-1" />
              {selectedLocation.name}
              {selectedLocation.branch_name && ` · ${selectedLocation.branch_name}`}
            </Badge>
          )}
          <p className="text-base sm:text-xl text-gray-300">
            {currentTime.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <p className="text-3xl sm:text-5xl font-mono font-bold mt-1 sm:mt-2 text-primary">
            {currentTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto">
        <p className="text-gray-400 text-center mb-3 sm:mb-4 text-sm sm:text-base">Mitarbeiter auswählen:</p>
        <EmployeeGrid employees={employees} loading={loading} onSelect={handleEmployeeSelect} />
      </div>
    </div>
  );
}
